import { useState } from 'react'
import { ChallengeService } from '@duck/api-definitions'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@duck/ui'
import { useTar } from '@/modules'
import { useBootChallengeWithWeapon, WeaponPickCancelledError } from '@/hooks/useBootChallengeWithWeapon'
import { getTimeLimitDisplay } from '@/utils/assignmentDisplay.utils'

type Assignment = ChallengeService.AvailableAssignment

/**
 * Status-aware start flow for an assignment, shared by the assignment card and
 * the detail drawer. Booting passes `assignmentId`; an Available assignment with
 * a time limit first surfaces a confirmation dialog (rendered via the returned
 * `timeLimitDialog`) before it actually boots.
 */
export const useStartAssignment = () => {
  const { t } = useTar()
  const navigate = useNavigate()
  const bootChallenge = useBootChallengeWithWeapon()
  const [isLoading, setIsLoading] = useState(false)
  const [pending, setPending] = useState<Assignment | null>(null)

  const doStart = async (assignment: Assignment) => {
    setIsLoading(true)
    try {
      if (assignment.status === ChallengeService.AvailableChallengeStatusEnum.Available) {
        const resp = await bootChallenge({
          taskId: assignment.taskId,
          assignmentId: assignment.assignmentId,
          allowedWeapons: assignment.allowedWeapons,
        })
        const slug = resp.data.slug
        if (slug) {
          sessionStorage.setItem('challenge_unbooted', slug)
          navigate({ to: `/challenge/${slug}` })
          return
        }
      }
      if (assignment.slug && assignment.status === ChallengeService.AvailableChallengeStatusEnum.Unbooted) {
        sessionStorage.setItem('challenge_unbooted', assignment.slug)
        navigate({ to: `/challenge/${assignment.slug}` })
        return
      }
      if (assignment.slug && assignment.status === ChallengeService.AvailableChallengeStatusEnum.Ongoing) {
        navigate({ to: `/challenge/${assignment.slug}` })
        return
      }
    } catch (err) {
      if (err instanceof WeaponPickCancelledError) return
      toast.error(t('challenges.failedToStartChallenge'), { duration: 10000 })
    } finally {
      setIsLoading(false)
    }
  }

  const requestStart = (assignment: Assignment) => {
    const hasTimeLimit = assignment.timeLimit !== undefined && assignment.timeLimit !== null && assignment.timeLimit > 0
    if (assignment.status === ChallengeService.AvailableChallengeStatusEnum.Available && hasTimeLimit) {
      setPending(assignment)
      return
    }
    void doStart(assignment)
  }

  const timeLimitDialog = (
    <Dialog open={pending !== null} onOpenChange={(open) => { if (!open) setPending(null) }}>
      <DialogContent noMinHeight noCloseButton width="sm">
        <DialogHeader>
          <DialogTitle>{t('common.thisChallengeHasATimeLimit')}</DialogTitle>
        </DialogHeader>
        <DialogDescription asChild>
          <div className="flex flex-col gap-1 text-muted-foreground">
            <div>{t('common.timeLimitDescription', { timeLimit: getTimeLimitDisplay(pending?.timeLimit ?? 0, t) })}</div>
          </div>
        </DialogDescription>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setPending(null)}>{t('common.cancel')}</Button>
          <Button onClick={() => { const a = pending; setPending(null); if (a) void doStart(a) }}>{t('common.start')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )

  return { requestStart, isLoading, timeLimitDialog }
}
